import { useMemo } from "react";
import { useLanguage } from "../../context/Language/LanguageContext";
import { addStatsItem, calculateAllStats, createStatsItem } from "../utils/StatsItem";

// Single stat comparison row — split proportional bar with label centred
function StatRow({ label, aVal, bVal }) {
  const a = aVal ?? 0;
  const b = bVal ?? 0;
  const total = (a + b) || 1;
  const aPct = (a / total) * 100;
  const bPct = 100 - aPct;

  return (
    <div className="grid grid-cols-[3.5rem_1fr_6.5rem_1fr_3.5rem] items-center gap-x-2 py-1.5">
      {/* Team A value */}
      <span className="text-right text-sm font-bold tabular-nums text-primary">{a}</span>

      {/* Team A bar — fills from right toward centre */}
      <div className="h-1.5 bg-base-300 rounded-l-full overflow-hidden flex justify-end">
        <div className="bg-primary h-full transition-all duration-500" style={{ width: `${aPct}%` }} />
      </div>

      {/* Stat label */}
      <span className="text-center text-[10px] font-semibold uppercase tracking-widest text-base-content/45 leading-none">
        {label}
      </span>

      {/* Team B bar — fills from left toward centre */}
      <div className="h-1.5 bg-base-300 rounded-r-full overflow-hidden">
        <div className="bg-secondary h-full transition-all duration-500" style={{ width: `${bPct}%` }} />
      </div>

      {/* Team B value */}
      <span className="text-left text-sm font-bold tabular-nums text-secondary">{b}</span>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────────

function buildAggregateStats(match) {
  const aTotal = createStatsItem(null, 0);
  const bTotal = createStatsItem(null, 0);
  let aTPs = 0;
  let bTPs = 0;

  // per-player accumulator: guid -> { player, stats }
  const aPlayers = {};
  const bPlayers = {};

  for (const set of match.sets) {
    if (!set.teamAStatsItems || !set.teamBStatsItems) continue;

    aTPs += set.HomeScore ?? 0;
    bTPs += set.AwayScore ?? 0;

    addStatsItem(set.teamAStatsItems[0], aTotal);
    addStatsItem(set.teamBStatsItems[0], bTotal);

    // player stats (indices 1+)
    for (const ps of set.teamAStatsItems.slice(1)) {
      if (!ps?.player) continue;
      const g = ps.Guid || ps.player?.Guid;
      if (!g) continue;
      if (!aPlayers[g]) aPlayers[g] = { player: ps.player, stats: createStatsItem(ps.player, 0) };
      addStatsItem(ps, aPlayers[g].stats);
    }
    for (const ps of set.teamBStatsItems.slice(1)) {
      if (!ps?.player) continue;
      const g = ps.Guid || ps.player?.Guid;
      if (!g) continue;
      if (!bPlayers[g]) bPlayers[g] = { player: ps.player, stats: createStatsItem(ps.player, 0) };
      addStatsItem(ps, bPlayers[g].stats);
    }
  }

  aTotal.TotalPoints = aTPs;
  bTotal.TotalPoints = bTPs;
  calculateAllStats(aTotal);
  calculateAllStats(bTotal);

  const topScorers = (playerMap) =>
    Object.values(playerMap)
      .map(({ player, stats }) => ({
        player,
        points: (stats.Spike3 || 0) + (stats.Serve3 || 0) + (stats.BlckSolo || 0),
      }))
      .filter((p) => p.points > 0)
      .sort((a, b) => b.points - a.points)
      .slice(0, 3);

  return {
    aStats: aTotal,
    bStats: bTotal,
    aTopScorers: topScorers(aPlayers),
    bTopScorers: topScorers(bPlayers),
  };
}

// Derives the match score (sets won by each team) directly from the sets
// array. `match.HomeScore`/`match.AwayScore` are only ever computed by the
// DVW parser (incremented set-by-set as each set's final score is read) —
// the PSVB/VBStats parser never populates them, so a live VBStats match
// would otherwise always show "0-0" here even with completed sets.
//
// Live-coded sets (DVMatchCoding) carry an explicit `winner` ("A"/"B") once
// closed — either automatically (standard/custom winning score + margin) or
// manually via the "Close Set" override — so that's always trusted first.
// Imported DVW sets have no `winner` field, so fall back to detecting the
// winning condition (standard >=25/>=15-for-a-5th-set, or the match's own
// custom "Points to close a set" override from Input.jsx) with a 2-point
// lead (or 1-point if "Require 2-point advantage" was unchecked on import) so
// a still-in-progress set is never counted as a win.
function computeSetsWon(sets, winScore, winByTwo) {
  let aWins = 0;
  let bWins = 0;
  const margin = winByTwo === false ? 1 : 2;
  for (const s of sets ?? []) {
    if (s.winner === "A") {
      aWins++;
      continue;
    }
    if (s.winner === "B") {
      bWins++;
      continue;
    }
    const h = s.HomeScore ?? 0;
    const a = s.AwayScore ?? 0;
    const winThreshold =
      winScore != null ? winScore : (s.GameNumber ?? 0) >= 5 ? 15 : 25;
    if (h >= winThreshold && h >= a + margin) {
      aWins++;
    } else if (a >= winThreshold && a >= h + margin) {
      bWins++;
    }
  }
  return { aWins, bWins };
}

// ── main component ─────────────────────────────────────────────────────────

function MatchCentre({ match, isLive = false }) {
  const { t } = useLanguage();
  const { aStats, bStats, aTopScorers, bTopScorers } = useMemo(
    () => buildAggregateStats(match),
    [match]
  );
  const { aWins, bWins } = useMemo(
    () => computeSetsWon(match.sets, match.winScore, match.winByTwo),
    [match.sets, match.winScore, match.winByTwo]
  );

  const aOppErrors = Math.max(
    0,
    aStats.TotalPoints - (aStats.Spike3 || 0) - (aStats.BlockPoints || 0) - (aStats.Serve3 || 0)
  );
  const bOppErrors = Math.max(
    0,
    bStats.TotalPoints - (bStats.Spike3 || 0) - (bStats.BlockPoints || 0) - (bStats.Serve3 || 0)
  );

  const aSetTotal = (aStats.Set0 || 0) + (aStats.Set1 || 0) + (aStats.Set2 || 0) + (aStats.Set3 || 0);
  const bSetTotal = (bStats.Set0 || 0) + (bStats.Set1 || 0) + (bStats.Set2 || 0) + (bStats.Set3 || 0);

  const maxScorers = Math.max(aTopScorers.length, bTopScorers.length);

  const SectionLabel = ({ children }) => (
    <p className="text-[10px] font-bold uppercase tracking-widest text-center text-base-content/35 mb-1 mt-3">
      {children}
    </p>
  );

  return (
    <div className="max-w-xl mx-auto px-4 py-4 select-none">

      {/* ── Match header ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-2 mb-4">
        <span className="flex-1 text-base font-black text-primary leading-tight break-words text-left">
          {match.teamA.Name.toUpperCase()}
        </span>

        <div className="flex flex-col items-center gap-1 shrink-0">
          {isLive && (
            <span className="badge badge-error badge-sm font-bold text-[10px] tracking-wider animate-pulse">
              LIVE
            </span>
          )}
          <span className="text-3xl font-black tabular-nums tracking-tight leading-none">
            {aWins}&thinsp;–&thinsp;{bWins}
          </span>
          <div className="flex gap-1 flex-wrap justify-center">
            {match.sets.map((s) => {
              const aWins = (s.HomeScore ?? 0) > (s.AwayScore ?? 0);
              const bWins = (s.AwayScore ?? 0) > (s.HomeScore ?? 0);
              return (
                <span
                  key={s.GameNumber}
                  className={`text-xs tabular-nums px-1.5 py-0.5 rounded font-semibold ${
                    aWins
                      ? "bg-primary/15 text-primary"
                      : bWins
                      ? "bg-secondary/15 text-secondary"
                      : "bg-base-200 text-base-content/60"
                  }`}
                >
                  {s.HomeScore}–{s.AwayScore}
                </span>
              );
            })}
          </div>
        </div>

        <span className="flex-1 text-base font-black text-secondary leading-tight break-words text-right">
          {match.teamB.Name.toUpperCase()}
        </span>
      </div>

      {/* ── Team name header row ──────────────────────────────────────────── */}
      <div className="grid grid-cols-[3.5rem_1fr_6.5rem_1fr_3.5rem] gap-x-2 mb-1">
        <span className="col-start-1 col-span-2 text-[10px] font-bold uppercase tracking-widest text-primary/60 text-right pr-2">
          {match.teamA.Name}
        </span>
        <span />
        <span className="col-span-2 text-[10px] font-bold uppercase tracking-widest text-secondary/60 text-left pl-2">
          {match.teamB.Name}
        </span>
      </div>

      {/* ── Points Won ───────────────────────────────────────────────────── */}
      <SectionLabel>{t("Points Won")}</SectionLabel>
      <div className="bg-base-200/60 rounded-xl px-3 py-1">
        <StatRow label={t("Attack")} aVal={aStats.Spike3} bVal={bStats.Spike3} />
        <div className="border-t border-base-300/60" />
        <StatRow label={t("Block")} aVal={aStats.BlockPoints} bVal={bStats.BlockPoints} />
        <div className="border-t border-base-300/60" />
        <StatRow label={t("Serve")} aVal={aStats.Serve3} bVal={bStats.Serve3} />
        <div className="border-t border-base-300/60" />
        <StatRow label={t("Opp. Error")} aVal={aOppErrors} bVal={bOppErrors} />
        <div className="border-t border-base-content/20 my-0.5" />
        <StatRow label={t("Total")} aVal={aStats.TotalPoints} bVal={bStats.TotalPoints} />
      </div>

      {/* ── Match Skills ─────────────────────────────────────────────────── */}
      <SectionLabel>{t("Match Skills")}</SectionLabel>
      <div className="bg-base-200/60 rounded-xl px-3 py-1">
        <StatRow label={t("Dig")} aVal={aStats.Dig} bVal={bStats.Dig} />
        <div className="border-t border-base-300/60" />
        <StatRow label={t("Reception")} aVal={aStats.PassTotal} bVal={bStats.PassTotal} />
        <div className="border-t border-base-300/60" />
        <StatRow label={t("Set")} aVal={aSetTotal} bVal={bSetTotal} />
      </div>

      {/* ── Best Scorers ─────────────────────────────────────────────────── */}
      {maxScorers > 0 && (
        <>
          <SectionLabel>{t("Best Scorers")}</SectionLabel>
          <div className="bg-base-200/60 rounded-xl px-3 py-1">
            {Array.from({ length: maxScorers }, (_, i) => {
              const a = aTopScorers[i];
              const b = bTopScorers[i];
              return (
                <div
                  key={i}
                  className="grid grid-cols-[1fr_2.5rem_2.5rem_1fr] items-center gap-x-2 py-1.5"
                >
                  <span className="text-xs font-semibold truncate text-primary">
                    {a ? `${a.player.NickName?.toUpperCase()} (${a.player.shirtNumber})` : ""}
                  </span>
                  <span className="text-right text-sm font-bold tabular-nums text-primary">
                    {a?.points ?? ""}
                  </span>
                  <span className="text-left text-sm font-bold tabular-nums text-secondary">
                    {b?.points ?? ""}
                  </span>
                  <span className="text-xs font-semibold truncate text-right text-secondary">
                    {b ? `${b.player.NickName?.toUpperCase()} (${b.player.shirtNumber})` : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default MatchCentre;
